import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@workos-inc/authkit-react';
import api from '../http';

interface AssistantAvatarContextType {
  assistantAvatarUrl: string;
  isLoading: boolean;
  isLoadingPhoto: boolean;
  uploadAssistantPhoto: (file: File) => Promise<void>;
  setAssistantPhoto: (photoUrl: string) => Promise<void>;
  refreshAssistantPhoto: () => Promise<void>;
}

const AssistantAvatarContext = createContext<AssistantAvatarContextType | undefined>(undefined);

export const useAssistantAvatar = () => {
  const context = useContext(AssistantAvatarContext);
  if (context === undefined) {
    throw new Error('useAssistantAvatar must be used within an AssistantAvatarProvider');
  }
  return context;
};

interface AssistantAvatarProviderProps {
  children: ReactNode;
}

export const AssistantAvatarProvider: React.FC<AssistantAvatarProviderProps> = ({ children }) => {
  const [assistantAvatarUrl, setAssistantAvatarUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
  const { getAccessToken, user } = useAuth();

  const loadAssistantPhoto = async () => {
    // Only load assistant photo if user is authenticated
    if (!user) {
      return;
    }
    
    setIsLoadingPhoto(true);
    try {
      const token = await getAccessToken();
      const response = await api.getAssistantPhoto(token);
      if (response.assistant_photo_url) {
        setAssistantAvatarUrl(response.assistant_photo_url);
      }
    } catch (error) {
      console.error('Failed to load assistant photo:', error);
      setAssistantAvatarUrl('');
    } finally {
      setIsLoadingPhoto(false);
    }
  };

  const uploadAssistantPhoto = async (file: File) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await api.uploadAssistantPhoto(file, token);
      if (response.assistant_photo_url) {
        setAssistantAvatarUrl(response.assistant_photo_url);
      }
    } catch (error) {
      console.error('Failed to upload assistant photo:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const setAssistantPhoto = async (photoUrl: string) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await api.setAssistantPhoto(photoUrl, token);
      if (response.assistant_photo_url) {
        setAssistantAvatarUrl(response.assistant_photo_url);
      }
    } catch (error) {
      console.error('Failed to set assistant photo:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAssistantPhoto = async () => {
    await loadAssistantPhoto();
  };

  useEffect(() => {
    // Only load assistant photo when user is authenticated
    if (user) {
      loadAssistantPhoto();
    }
  }, [user]);

  const value: AssistantAvatarContextType = {
    assistantAvatarUrl,
    isLoading,
    isLoadingPhoto,
    uploadAssistantPhoto,
    setAssistantPhoto,
    refreshAssistantPhoto,
  };

  return (
    <AssistantAvatarContext.Provider value={value}>
      {children}
    </AssistantAvatarContext.Provider>
  );
}; 